'use client';

import { useGSAP } from "@gsap/react";
import AnchorNavBar from "./AnchorNavBar/AnchorNavBar";
import React, { useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ServiceCategorySectionContent } from '@/lib/types';
import { useLenis } from "@/contexts/LenisContext";

import { debug } from '@/lib/debug';
gsap.registerPlugin(useGSAP, ScrollTrigger)

interface AnchorNavWrapperProps {
  serviceCategorySectionPageData: ServiceCategorySectionContent
}

// Create a new component (can be in the same file or separate)
const AnchorNavWrapper: React.FC<AnchorNavWrapperProps> = React.memo(({ serviceCategorySectionPageData }) => {
  const { lenis } = useLenis();
  const [showAnchorNav, setShowAnchorNav] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');

  const handleCategoryClick = (categoryNavigationLabel: string) => {
    const section = document.getElementById(categoryNavigationLabel);
    if (section) {
      debug.log('clicked ', categoryNavigationLabel)

      // Emit event to hide navbar during smooth scroll
      window.dispatchEvent(new CustomEvent('lenisScrollStart'));

      const yOffset = -window.innerHeight / 2 + section.getBoundingClientRect().height / 2;
      const y = section.getBoundingClientRect().top + window.scrollY + yOffset;
      // Disable user scroll during animation

      lenis?.scrollTo(y, {
        offset: 0,
        duration: 2, // TODO: this can be based on the offset between current scroll and target scroll.             
        easing: (t: number) => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
        immediate: false,
        lock: true,
        onComplete: () => {
          // Re-enable user scroll and navbar
          window.dispatchEvent(new CustomEvent('lenisScrollEnd'));
        }
      });
    }
  };

  useGSAP(() => {
    const heroSection = document.querySelector('.hero-section');
    const ctaSection = document.querySelector('.cta-section');

    if (heroSection && ctaSection) {
      // Anchor nav visibility triggers
      ScrollTrigger.create({
        trigger: heroSection,
        start: "top 20%",
        end: "bottom 85%",
        // markers: true,
        onEnter: () => setShowAnchorNav(false),
        onLeaveBack: () => setShowAnchorNav(true),
        onEnterBack: () => setShowAnchorNav(false),
        onLeave: () => setShowAnchorNav(true),
      });

      ScrollTrigger.create({
        trigger: ctaSection,
        start: "top bottom",
        end: "bottom bottom",
        // markers: true,
        onEnter: () => setShowAnchorNav(false),
        onLeaveBack: () => setShowAnchorNav(true),
      });
    }

    // Create ScrollTriggers for each service category section to track active section
    debug.log('----------------- serviceCategorySectionPageData.categories', serviceCategorySectionPageData.categories);
    serviceCategorySectionPageData.categories.forEach((category, index) => {
      // const section = document.getElementById(category.serviceCategoryTitle);
      const section = document.getElementById(category.serviceCategoryNavigationLabel);
      if (section) {
        debug.log('----------------- found section', section);
        ScrollTrigger.create({
          trigger: section,
          start: "top 60%",
          end: "bottom 40%",
          // markers: true,
          onEnter: () => {
            debug.log('Entering section:', category.serviceCategoryTitle);
            setActiveSection(category.serviceCategoryNavigationLabel);
          },
          onEnterBack: () => {
            debug.log('Entering back section:', category.serviceCategoryTitle);
            setActiveSection(category.serviceCategoryNavigationLabel);
          },
          onLeaveBack: () => {
            // When leaving backwards, highlight the previous section if it exists
            debug.log('Leaving back section:', category.serviceCategoryNavigationLabel);
            if (index > 0) {
              const previousSection = serviceCategorySectionPageData.categories[index - 1];
              setActiveSection(previousSection.serviceCategoryNavigationLabel);
            } else {
              // No previous section, clear active state
              setActiveSection('');
            }
          }
        });
      }
    });

  }, [serviceCategorySectionPageData]);

  return (
    <div 
    style={{
      zIndex: 999
    }}
    className={`
            fixed bottom-0 left-0 right-0 pb-[2rem] px-[3.5rem] w-full flex justify-center
            transition-opacity duration-300 
                ${showAnchorNav ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
            `}>
      <div className="w-full container-x-large flex justify-center">
        <AnchorNavBar
          buttons={serviceCategorySectionPageData.categories.map((category) => category.serviceCategoryNavigationLabel)}
          // buttons={serviceCategorySectionPageData.categories.map((category) => category.serviceCategoryTitle)}
          onButtonClick={handleCategoryClick}
          activeButton={activeSection}
        />
      </div>
    </div>
  );
});

export default AnchorNavWrapper;